import ReactDOMServer from "react-dom/server";
import { connect } from "react-redux";
import moment from "moment";
import isEqual from "lodash-es/isEqual";
import debounce from "lodash-es/debounce";
import classNames from "classnames";
import { toast } from "extensions/toast";
import { captureException } from "extensions/sentry";

import { v2 } from "common/config/api";
import { number } from "common/formats";

import { ErrorFetchingTransactionsDetails, NoDataAvailable, ApiDroppedRequest } from "common/helpers/errors";
import { ErrorView } from "common/components/error";
import { ConfigurableTable } from "common/components/configTable/table";
import { ExportDialog } from "common/components/configTable/tableExport";
import { Qualifiers } from "common/components/queryBuilder/qualifiers";
import { Modal } from "common/components/modal";
import { AdminsListRedux } from "common/components/admins";
import { getters } from "common/components/getters/getters";

import { ADD_QUALIFIER } from "common/actions/common";
import { FETCH_TRANSACTIONS } from "common/actions/analytics";
import { FETCH_TABLE_CONFIG, SAVE_TABLE_CONFIG, UPDATE_STORED_CONFIG } from "common/actions/components";

import { table_mapper } from "./fields";
import { SingleTransaction } from "./singleTransaction";

import "./transactionsTable.scss";

const mapStateToProps = (state) => ({
    date_range: state.common.date_range,
    currency: state.common.currency,
    qualifiers: state.common.qualifiersStr,
    product: state.common.product,
    table_configs: state.components.table_configs.transactions,
    haveAutomaticDownload: state.common.user.superuser
});

export class BaseTable extends ConfigurableTable {
    constructor(props) {
        super(props);
        this.state = {
            hasError: false,
            isLoading: true,
            fullView: false,
            page: 1,
            count: 0,
            data: [],
            config: { data: [] },
            configVisible: false,
            exportVisible: false,
            singleTxnVisible: !!props.queryParams.txn_id,
            singleTxnID: props.queryParams.txn_id
        };
        this.ui = null;
    }

    componentDidUpdate(prevProps) {
        const { date_range, qualifiers } = this.props;
        if (qualifiers !== prevProps.qualifiers || !isEqual(date_range, prevProps.date_range)) {
            this.fetchData({ q: qualifiers, page: 1 }).then(this.updateColumns).then(this.enforceTableView);
        }
        this.adjustDatatable();
    }

    exportTable = () => {
        this.showExport();
    };

    getExportOptions = () => {
        const { suggestedNamePrefix, date_range } = this.props;
        let startDate;
        let endDate;
        let datetype = date_range?.datetype || "activity";
        if (suggestedNamePrefix === "search") {
            const qualifiers = new Qualifiers({ value: this.props.qualifiers });
            [startDate] = qualifiers.get("activity_date_start");
            [endDate] = qualifiers.get("activity_date_end");
            if (startDate) {
                startDate = moment(startDate).startOf("day").toISOString();
            }
            if (endDate) {
                endDate = moment(endDate).endOf("day").toISOString();
            }
            datetype = "activity";
        } else {
            startDate = date_range.startDate;
            endDate = date_range.endDate;
        }

        if (this.props.product === "Reconciliation") {
            datetype = "settlement";
        }

        const filters = {
            start: startDate || "",
            end: endDate || "",
            datetype,
            q: this.props.qualifiers
        };
        const totalTxns = this.state.count;
        let suggested_name;
        if (filters.start && filters.end) {
            suggested_name = `${suggestedNamePrefix}_txns_${moment.utc(filters.start).format("YYYYMMDD")}-${moment.utc(filters.end).format("YYYYMMDD")}`;
        } else {
            suggested_name = `${suggestedNamePrefix}_txns_${moment().format("YYYYMMDD")}`;
        }

        return {
            filters,
            resource: "transactions",
            section: this.props.section,
            formats: [
                {
                    active: true,
                    value: "csv",
                    label: "CSV"
                },
                {
                    active: totalTxns === null || totalTxns < 1_048_576,
                    value: "xlsx",
                    label: "XLSX"
                }
            ],
            suggested_name
        };
    };

    adjustDatatable = () => {
        if (this.table_ref.current) this.table_ref.current.table.current.ui.adjust();
    };

    updateColumns = () => {
        const columns = [...this.getControlColumn(), ...this.getColumnsFromConfig()];
        try {
            this.table_ref.current?.table.current.ui.refreshColumns(columns);
        } catch (error) {
            captureException(error, { extra: { func_ref: "this.updateColumns()" } });
        }
    };

    getUiOptions = () => ({
        scrollX: true,
        leftSplit: 1,
        minHeight: 330,
        hover: "row-hover",
        select: "row",
        tooltip: {
            dx: 10,
            dy: -40
        },
        onClick: {
            queryable: this.actionWrapper(({ id, obj, columnConfig }) => ADD_QUALIFIER(`${columnConfig.cellOptions.pivot_alias || id.column}:${Qualifiers.normalizeValue(obj[id.column])}`))
            // transdetail: this.actionWrapper(({ id, obj, webix }) => { this.showTransaction(obj._id); webix.select(id, false); })
        }
    });

    getOnEvents = (self = this) => ({
        onSelectChange: this.actionWrapper(({ webix }) => {
            const obj = webix.getSelectedItem();
            if (obj) this.showTransaction(obj._id);
        }),
        onBeforeSelect(selection) {
            return selection.column === "control";
        },
        onAfterLoad() {
            if (self.state.fullView) {
                this.showColumnBatch("expanded", true); // show "expanded", preserve state of others
            } else {
                this.showColumnBatch("expanded", false); // hide "expanded", preserve state of others
            }
        }
    });

    setTableUI = () => {
        // const self = this;
        this.ui = this.getWebixConf({
            noDataErrorProps: {
                header: "No data to display.",
                message: "We couldn't find any transactions for your request"
            }
        });
    };

    // TODO: refactor to use search api endpoints
    getEndpointUrl = (section) => {
        switch (section) {
        case "authorizations": {
            return v2.authorizations.transactions;
        }
        case "settlements": {
            return v2.settlements.transactionsAll;
        }
        case "fees": {
            return v2.fees.transactions;
        }
        case "chargebacks": {
            return v2.chargebacks.transactions;
        }
        case "cashflow": {
            return v2.cashflow.transactions;
        }
        case "all_fraud": {
            return v2.fraud.transactionsAll;
        }
        case "psd2_fraud": {
            return v2.psd2.transactionsAll;
        }
        case "fraudsight": {
            return v2.fraudsight.transactionsAll;
        }
        case "threedsflex": {
            return v2.threedsflex.transactionsAll;
        }
        case "misc": {
            return v2.misc.transactions;
        }
        default: {
            throw new Error("No url params specified");
        }
        }
    };

    fetchTableConfig = ({ configId, apiSubresource } = this.props) => {
        // TODO: indicate that foreign cofig used
        if (configId) {
            FETCH_TABLE_CONFIG({ configId }).then((res) => {
                // take note about config levels: user -> merchant -> pazien | we don't use merchant config now
                const configs = res.filter(({ section }) => section === this.props.section || section === apiSubresource);
    
                if (configs.length > 1) {
                    const config = configs.find(({ level }) => level === "user");
                    this.setState({ config }, this.setTableUI);
                } else {
                    this.setState({ config: configs[0] }, this.setTableUI);
                }
            }).catch(
                (error) => {
                    this.setState({ hasError: true, error: { error, capture: true } });
                }
            );
        } else {
            const { table_configs, section } = this.props;
            const config = table_configs?.user?.[section] || table_configs?.pazien?.[section];
            if (config) {
                this.setState({ config }, this.setTableUI);
            } else {
                const error = new Error("Table configs did not load");
                this.setState({ hasError: true, error: { error, capture: true } });
            }
        }
    };

    cleanPromises = () => {
        this.promises.forEach((promise) => promise.cancel());
        this.promises = [];
    };
    
    fetchData = (options = {}) => {
        const { startDate, endDate, datetype } = this.props.date_range;
        this.cleanPromises();
        this.setState({ isLoading: true });
        const promise = FETCH_TRANSACTIONS({
            url: this.getEndpointUrl(this.props.section),
            start: startDate,
            end: endDate,
            datetype,
            limit: 100,
            currency: this.props.currency,
            page: this.state.page,
            q: this.props.qualifiers?.toString(true),
            ...options
        }).then(
            ({ results, ...rest }) => {
                this.setState({
                    isLoading: false, data: results, ...rest
                });
                return results;
            }
        ).catch(this.catchFetchErrors);
        
        this.promises.push(promise);
        return promise;
    };
    
    catchFetchErrors = (originalError) => {
        let error;
        let capture = true;
        if (originalError.status === 403) {
            error = new ErrorFetchingTransactionsDetails({
                custom: true,
                message: "You do not have permission to view transaction details. If you believe this is an error, please contact your admins:",
                info: <AdminsListRedux />,
                hideDetails: true,
                actions: false,
                originalError
            });
            capture = false;
        } else if (originalError.status === 504 || (originalError.status === 0 && originalError.extra.duration > 6000)) { // duration > 1min with 0 status code indicate timed out request
            error = new ApiDroppedRequest({
                custom: true,
                header: "Sorry, request takes longer than expected",
                message: "Please, try again in a few moments or try to narrow your search.",
                hideDetails: true,
                originalError
            });
        } else if (originalError.status === 204) {
            error = new NoDataAvailable({
                custom: true,
                header: "No data available",
                message: "Try expanding your date range or removing any applied filters.",
                actions: false,
                hideDetails: true,
                originalError
            });
            capture = false;
        } else {
            error = new ErrorFetchingTransactionsDetails({
                custom: true,
                message: "There was an error fetching transactions",
                actions: true,
                tags: {
                    status: this.state.has_error
                },
                originalError
            });
        }
        this.setState({
            isLoading: false, data: [], hasError: true, error: { error, capture }
        });
    };

    isOverThreshold = () => this.state.count > 1_000_000;

    getColumnsFromConfig = ({ forceFullView = false } = {}) => this.state.config.data
        .map(table_mapper(forceFullView))
        .map((column, index, arr) => {
            const getter = getters.findById(column.getter);
            const { preFormat } = column;
            
            if (column.cellOptions.queryable && !this.props.noClickFiltering) {
                column.template = (obj) => `<div class="queryable">${preFormat(obj)}</div>`;
            } else {
                column.template = preFormat;
            }

            column.tooltip = false;

            return {
                ...column,
                id: getter.key,
                header: {
                    text: ReactDOMServer.renderToStaticMarkup(
                        <div className="flex-content" data-getter-ref={column.getter}>
                            <div className="header">{getter.name}</div>
                        </div>
                    ),
                    tooltip: column.headerTooltip
                },
                // fillspace: index === arr.length - 1, // does not work properly (try webix_ss_center != webix_ss_center_scroll)
                adjust: true
            };
        });

    getControlColumn = () => [{
        id: "control",
        header: "View",
        tooltip: null,
        css: "control-column",
        batch: "collapsed",
        adjust: true,
        template: "<div class='flex-content'><div class='controls'><div class='control-icon transdetail'><i class='fal fa-search-plus'></i></div></div></div>"
    }];

    pageHandler = (page) => {
        this.fetchData({ page });
        this.setTableUI();
    };

    showConfig = () => this.setState({ configVisible: true });

    hideConfig = () => this.setState({ configVisible: false });

    showExport = () => this.setState({ exportVisible: true });

    hideExport = () => this.setState({ exportVisible: false });

    showTransaction = debounce((id) => this.setState({ singleTxnVisible: true, singleTxnID: id }), 100);

    hideTransaction = () => this.setState({ singleTxnVisible: false }, this.table_ref.current?.table.current?.ui.unselectAll());

    handleConfigSave = (config) => {
        SAVE_TABLE_CONFIG(config)
            .then((res) => { UPDATE_STORED_CONFIG(res); this.setState({ config: res, fullView: false }); })
            .then(this.updateColumns)
            .then(this.enforceTableView)
            .then(this.hideConfig)
            .catch((error) => { toast.error("There was a problem saving your config. Try again later."); captureException(error); });
    };

    getPazienConfig = ({ table_configs, section } = this.props) => table_configs.pazien[section];

    toggleView = () => {
        this.setState((prevState) => ({ fullView: !prevState.fullView }), this.enforceTableView);
    };

    enforceTableView = () => {
        const webixEl = this.table_ref?.current?.table?.current?.ui;
        if (webixEl) {
            webixEl.callEvent("onAfterLoad");
        }
    };
    
    render() {
        const { page, count, next, isLoading } = this.state;
        if (this.state.hasError) {
            const { error, capture } = this.state.error;
            return (
                <ErrorView
                    error={error}
                    hideDetails
                    captureInSentry={capture}
                    custom
                />
            );
        }
        
        return (
            <div className={classNames("configurable-table-view", this.props.className, this.props.section)}>
                <Modal
                    close={this.hideExport}
                    header={count ? `Export ${number(count)} transactions` : "Export transactions"}
                    portalId="modal-export-transactions"
                    show={this.state.exportVisible}
                >
                    <ExportDialog
                        config={this.state.config}
                        section={this.props.section}
                        exportOptions={this.getExportOptions()}
                        haveAutomaticDownload={this.props.haveAutomaticDownload}
                    />
                </Modal>
                <Modal
                    close={this.hideConfig}
                    header="Table options"
                    portalId="modal-table-options"
                    show={this.state.configVisible}
                >
                    {this.renderConfig()}
                </Modal>
                <Modal
                    close={this.hideTransaction}
                    modal={false}
                    noPadding
                    overlay={false}
                    portalId="modal-single-transaction"
                    show={this.state.singleTxnVisible}
                >
                    <SingleTransaction
                        className="vertical-transaction"
                        section={this.props.section}
                        txn_id={this.state.singleTxnID}
                        txn_data={this.state.data.find((txn) => txn._id === this.state.singleTxnID)}
                        config={this.state.config}
                    />
                </Modal>
                {this.props.showControls && this.renderControls()}
                {this.state.isLoading ? <ConfigurableTable.TablePlaceholder columns={5} rows={2} /> : <ConfigurableTable.WebixDatatable className={classNames("configurable-table", this.props.className)} ref={this.table_ref} ui={this.ui} data={this.state.data} />}
                {this.props.paginatorBottom
                    && (
                    <ConfigurableTable.Paginator
                        page={page}
                        count={count}
                        next={next}
                        loading={isLoading}
                        limit={100}
                        onPageChange={this.pageHandler}
                        results_length={this.state.data.length}
                    />
                    )}
            </div>
        );
    }
}

export const TransactionsTable = connect(mapStateToProps)(BaseTable);
